import React from 'react';
import PropTypes from 'prop-types';

import { Group } from '@vx/group';
import { LinePath } from '@vx/shape';
import { max } from 'd3-array';

const MILLISECONDS_PER_DAY = 86400000;

// Copy the next value (the previous price change) and set yesterdays date on
// it. This is needed because we want straight lines between the points, we
// want the line to go up first on the day of the price change.
const copyNextValue = (values, index) => {
  const currentValue = values[index];
  const nextValue = values[index + 1];

  if (
    values.length - 1 === index ||
    (currentValue.date - nextValue.date) / MILLISECONDS_PER_DAY === 1
  ) {
    return [currentValue];
  }

  const previousDate = new Date(currentValue.date);
  previousDate.setDate(previousDate.getDate() - 1);

  return [currentValue, { ...nextValue, date: previousDate }];
};

export const getCurves = (prices, colors) =>
  prices.map(price => ({
    id: price.id,
    values: price.prices
      .map(d => ({
        date: new Date(d.latestPriceChange.time),
        price: d.price,
      }))
      .reduce((acc, x, i, src) => acc.concat(copyNextValue(src, i)), []),
    store: price.store,
    color: colors[price.id],
  }));

export const getMaxYValue = curves =>
  max(curves.map(curve => max(curve.values, v => v.price))) || 0;

const x = d => d.date;
const y = d => d.price;

const inDateRange = (startDate, endDate) => value =>
  value.date >= startDate && value.date <= endDate;

const StorePriceCurve = props => {
  const { margin, xScale, yScale, curves, startDate, endDate } = props;

  return (
    <Group top={margin.top} left={margin.left}>
      {curves.map(curve => (
        <LinePath
          key={curve.id}
          data={curve.values.filter(inDateRange(startDate, endDate))}
          x={d => xScale(x(d))}
          y={d => yScale(y(d))}
          stroke={curve.color}
          strokeWidth={3}
        />
      ))}
    </Group>
  );
};

StorePriceCurve.propTypes = {
  margin: PropTypes.shape({
    top: PropTypes.number,
    left: PropTypes.number,
  }).isRequired,
  xScale: PropTypes.func.isRequired,
  yScale: PropTypes.func.isRequired,
  curves: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string,
      values: PropTypes.arrayOf(
        PropTypes.shape({
          date: PropTypes.object,
          price: PropTypes.number,
        })
      ),
    })
  ).isRequired,
  startDate: PropTypes.object.isRequired,
  endDate: PropTypes.object.isRequired,
};

export default StorePriceCurve;
